import { memo, useMemo } from 'react'

import { DataTableHeaderShell } from './data-table-header/data-table-header-shell'
import { DataTableHeaderCell } from './data-table-header-cell'
import { createColumnGridTemplate } from './data-table-types'

import type { DataTableModel } from '../atoms'
import type { Column } from './data-table-types'
import type { ClassNameProps } from '@root/shared/react'

type DataTableHeaderProps<
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = {
  columns: ReadonlyArray<Column<D, ColumnId, Context>>
  model: DataTableModel<D, ColumnId, object, Context>
  context: Context
} & ClassNameProps

const DataTableHeader_ = memo(
  ({ className, columns, model, context }: DataTableHeaderProps<object>) => {
    const gridTemplateColumns = useMemo(
      () => createColumnGridTemplate(columns),
      [columns],
    )

    const renderHeader = (column: Column) => {
      if (column.hidden) {
        return undefined
      }
      if (column.renderHeader) {
        return column.renderHeader({
          model,
          column,
          context,
        })
      }

      return (
        <DataTableHeaderCell
          key={column.id}
          column={column}
          model={model}
          context={context}
        />
      )
    }

    return (
      <DataTableHeaderShell
        className={className}
        style={{ gridTemplateColumns }}
        data-table-header
      >
        {columns.map(renderHeader)}
      </DataTableHeaderShell>
    )
  },
)

export const DataTableHeader = <
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
>(
  props: DataTableHeaderProps<D, ColumnId, Context>,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <DataTableHeader_ {...(props as any)} />
}
